<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import { db } from '../../lib/firebase';
  import { collection, query, where, orderBy, limit, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import { validateABN, formatABN } from '../../lib/abn';
  import type { CreditApplication } from '../../lib/types';
  import { CREDIT_APPLICATION_STATUSES, AUSTRALIAN_STATES } from '../../lib/types';

  // State
  let loading = $state(true);
  let submitting = $state(false);
  let error = $state('');
  let successMessage = $state('');
  let existingApplication = $state<CreditApplication | null>(null);

  // Form state
  let formData = $state({
    businessName: '',
    abn: '',
    tradingName: '',
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    contactPosition: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingSuburb: '',
    billingState: '',
    billingPostcode: '',
    billingCountry: 'Australia',
  });

  // Validation state
  let abnError = $state('');
  let formErrors = $state<Record<string, string>>({});

  // Load existing application on mount
  onMount(() => {
    if (!authState.user) {
      loading = false;
      return;
    }

    const applicationsRef = collection(db, 'credit_applications');
    const q = query(
      applicationsRef,
      where('userId', '==', authState.user.uid),
      orderBy('submittedAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const data = doc.data();
        existingApplication = {
          id: doc.id,
          ...data,
          submittedAt: data.submittedAt?.toDate?.() || data.submittedAt,
          reviewedAt: data.reviewedAt?.toDate?.() || data.reviewedAt,
        } as CreditApplication;

        // If rejected, pre-fill form with existing data for resubmission
        if (existingApplication.status === 'rejected') {
          formData = {
            businessName: existingApplication.businessName || '',
            abn: existingApplication.abn || '',
            tradingName: existingApplication.tradingName || '',
            contactFirstName: existingApplication.contactFirstName || '',
            contactLastName: existingApplication.contactLastName || '',
            contactEmail: existingApplication.contactEmail || '',
            contactPhone: existingApplication.contactPhone || '',
            contactPosition: existingApplication.contactPosition || '',
            billingAddressLine1: existingApplication.billingAddressLine1 || '',
            billingAddressLine2: existingApplication.billingAddressLine2 || '',
            billingSuburb: existingApplication.billingSuburb || '',
            billingState: existingApplication.billingState || '',
            billingPostcode: existingApplication.billingPostcode || '',
            billingCountry: existingApplication.billingCountry || 'Australia',
          };
        }
      } else {
        existingApplication = null;
      }
      loading = false;
    }, (err) => {
      console.error('Error loading credit application:', err);
      error = 'Failed to load credit application status.';
      loading = false;
    });

    return () => unsubscribe();
  });

  // ABN formatting and validation
  function handleAbnInput(e: Event) {
    const input = e.target as HTMLInputElement;
    const formatted = formatABN(input.value);
    formData.abn = formatted;

    // Clear previous error
    abnError = '';

    // Validate if complete (11 digits)
    const cleanABN = formatted.replace(/\s/g, '');
    if (cleanABN.length === 11) {
      if (!validateABN(cleanABN)) {
        abnError = 'Invalid ABN. Please check and try again.';
      }
    }
  }

  // Form validation
  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (!formData.businessName.trim()) errors.businessName = 'Business name is required';
    if (!formData.abn.trim()) errors.abn = 'ABN is required';
    else if (abnError) errors.abn = abnError;
    else if (!validateABN(formData.abn)) errors.abn = 'Invalid ABN';

    if (!formData.contactFirstName.trim()) errors.contactFirstName = 'First name is required';
    if (!formData.contactLastName.trim()) errors.contactLastName = 'Last name is required';
    if (!formData.contactEmail.trim()) errors.contactEmail = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) errors.contactEmail = 'Invalid email format';
    if (!formData.contactPhone.trim()) errors.contactPhone = 'Phone is required';

    if (!formData.billingAddressLine1.trim()) errors.billingAddressLine1 = 'Address is required';
    if (!formData.billingSuburb.trim()) errors.billingSuburb = 'Suburb is required';
    if (!formData.billingState) errors.billingState = 'State is required';
    if (!formData.billingPostcode.trim()) errors.billingPostcode = 'Postcode is required';
    else if (!/^\d{4}$/.test(formData.billingPostcode)) errors.billingPostcode = 'Invalid postcode';

    formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  // Submit application
  async function handleSubmit(e: Event) {
    e.preventDefault();
    error = '';
    successMessage = '';

    if (!validateForm()) return;
    if (!authState.user) {
      error = 'You must be logged in to submit an application.';
      return;
    }

    submitting = true;

    try {
      const applicationsRef = collection(db, 'credit_applications');
      await addDoc(applicationsRef, {
        userId: authState.user.uid,
        businessId: authState.user.uid, // For AccountAdmin, businessId is their userId
        businessName: formData.businessName.trim(),
        abn: formData.abn.replace(/\s/g, ''), // Store without spaces
        tradingName: formData.tradingName.trim() || null,
        contactFirstName: formData.contactFirstName.trim(),
        contactLastName: formData.contactLastName.trim(),
        contactEmail: formData.contactEmail.trim().toLowerCase(),
        contactPhone: formData.contactPhone.trim(),
        contactPosition: formData.contactPosition.trim() || null,
        billingAddressLine1: formData.billingAddressLine1.trim(),
        billingAddressLine2: formData.billingAddressLine2.trim() || null,
        billingSuburb: formData.billingSuburb.trim(),
        billingState: formData.billingState,
        billingPostcode: formData.billingPostcode.trim(),
        billingCountry: formData.billingCountry,
        status: 'pending',
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      successMessage = 'Your credit application has been submitted successfully. We will review it and get back to you shortly.';
    } catch (err) {
      console.error('Error submitting credit application:', err);
      error = 'Failed to submit application. Please try again.';
    } finally {
      submitting = false;
    }
  }

  // Get status display info
  function getStatusInfo(status: string) {
    return CREDIT_APPLICATION_STATUSES.find(s => s.id === status) || CREDIT_APPLICATION_STATUSES[0];
  }

  // Format date for display
  function formatDate(date: Date | string | undefined): string {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
</script>

<div class="credit-container">
  <div class="page-header">
    <h1>Freight Credit Application</h1>
    <p>Complete this application to enable freight quote functionality in the EQUOS app.</p>
  </div>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
  {:else if existingApplication && existingApplication.status !== 'rejected'}
    <!-- Show application status -->
    <div class="status-card">
      <div class="status-header">
        <div class="status-icon" style="background-color: {getStatusInfo(existingApplication.status).color}20; color: {getStatusInfo(existingApplication.status).color}">
          {#if existingApplication.status === 'pending'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          {:else if existingApplication.status === 'approved'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <path d="M22 4L12 14.01l-3-3" />
            </svg>
          {/if}
        </div>
        <div class="status-text">
          <h2>Application {getStatusInfo(existingApplication.status).label}</h2>
          <p>Submitted on {formatDate(existingApplication.submittedAt)}</p>
        </div>
        <span class="status-badge" style="background-color: {getStatusInfo(existingApplication.status).color}20; color: {getStatusInfo(existingApplication.status).color}">
          {getStatusInfo(existingApplication.status).label}
        </span>
      </div>

      <div class="application-details">
        <div class="detail-section">
          <h3>Business Information</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <label>Business Name</label>
              <span>{existingApplication.businessName}</span>
            </div>
            <div class="detail-item">
              <label>ABN</label>
              <span>{formatABN(existingApplication.abn)}</span>
            </div>
            {#if existingApplication.tradingName}
              <div class="detail-item">
                <label>Trading Name</label>
                <span>{existingApplication.tradingName}</span>
              </div>
            {/if}
          </div>
        </div>

        <div class="detail-section">
          <h3>Contact Details</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <label>Contact Name</label>
              <span>{existingApplication.contactFirstName} {existingApplication.contactLastName}</span>
            </div>
            <div class="detail-item">
              <label>Email</label>
              <span>{existingApplication.contactEmail}</span>
            </div>
            <div class="detail-item">
              <label>Phone</label>
              <span>{existingApplication.contactPhone}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3>Billing Address</h3>
          <p class="address-text">
            {existingApplication.billingAddressLine1}<br>
            {#if existingApplication.billingAddressLine2}{existingApplication.billingAddressLine2}<br>{/if}
            {existingApplication.billingSuburb}, {existingApplication.billingState} {existingApplication.billingPostcode}<br>
            {existingApplication.billingCountry}
          </p>
        </div>
      </div>

      {#if existingApplication.status === 'pending'}
        <div class="status-note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          <p>Your application is being reviewed. You will be notified once a decision has been made.</p>
        </div>
      {:else if existingApplication.status === 'approved'}
        <div class="status-note success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <path d="M22 4L12 14.01l-3-3" />
          </svg>
          <p>Your credit application has been approved. You can now use freight quote functionality in the EQUOS app.</p>
        </div>
      {/if}
    </div>
  {:else}
    <!-- Show application form -->
    {#if existingApplication?.status === 'rejected'}
      <div class="rejection-notice">
        <div class="notice-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M15 9l-6 6" />
            <path d="M9 9l6 6" />
          </svg>
        </div>
        <div class="notice-content">
          <h3>Previous Application Rejected</h3>
          <p>{existingApplication.rejectionReason || 'Your previous application was not approved. Please review and resubmit with corrected information.'}</p>
        </div>
      </div>
    {/if}

    {#if successMessage}
      <div class="success-message">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <path d="M22 4L12 14.01l-3-3" />
        </svg>
        <p>{successMessage}</p>
      </div>
    {:else}
      <form class="application-form" onsubmit={handleSubmit}>
        {#if error}
          <div class="error-message">{error}</div>
        {/if}

        <!-- Business Information -->
        <div class="form-section">
          <h2>Business Information</h2>
          <div class="form-grid">
            <div class="form-field">
              <label for="businessName">Business Name *</label>
              <input
                type="text"
                id="businessName"
                bind:value={formData.businessName}
                class:error={formErrors.businessName}
                placeholder="Enter your business name"
              />
              {#if formErrors.businessName}<span class="field-error">{formErrors.businessName}</span>{/if}
            </div>

            <div class="form-field">
              <label for="abn">ABN *</label>
              <input
                type="text"
                id="abn"
                value={formData.abn}
                oninput={handleAbnInput}
                class:error={formErrors.abn || abnError}
                placeholder="XX XXX XXX XXX"
                maxlength="14"
              />
              {#if formErrors.abn}<span class="field-error">{formErrors.abn}</span>
              {:else if abnError}<span class="field-error">{abnError}</span>{/if}
            </div>

            <div class="form-field full-width">
              <label for="tradingName">Trading Name (if different)</label>
              <input
                type="text"
                id="tradingName"
                bind:value={formData.tradingName}
                placeholder="Enter trading name"
              />
            </div>
          </div>
        </div>

        <!-- Contact Details -->
        <div class="form-section">
          <h2>Contact Details</h2>
          <div class="form-grid">
            <div class="form-field">
              <label for="contactFirstName">First Name *</label>
              <input
                type="text"
                id="contactFirstName"
                bind:value={formData.contactFirstName}
                class:error={formErrors.contactFirstName}
                placeholder="Enter first name"
              />
              {#if formErrors.contactFirstName}<span class="field-error">{formErrors.contactFirstName}</span>{/if}
            </div>

            <div class="form-field">
              <label for="contactLastName">Last Name *</label>
              <input
                type="text"
                id="contactLastName"
                bind:value={formData.contactLastName}
                class:error={formErrors.contactLastName}
                placeholder="Enter last name"
              />
              {#if formErrors.contactLastName}<span class="field-error">{formErrors.contactLastName}</span>{/if}
            </div>

            <div class="form-field">
              <label for="contactEmail">Email *</label>
              <input
                type="email"
                id="contactEmail"
                bind:value={formData.contactEmail}
                class:error={formErrors.contactEmail}
                placeholder="email@company.com"
              />
              {#if formErrors.contactEmail}<span class="field-error">{formErrors.contactEmail}</span>{/if}
            </div>

            <div class="form-field">
              <label for="contactPhone">Phone *</label>
              <input
                type="tel"
                id="contactPhone"
                bind:value={formData.contactPhone}
                class:error={formErrors.contactPhone}
                placeholder="04XX XXX XXX"
              />
              {#if formErrors.contactPhone}<span class="field-error">{formErrors.contactPhone}</span>{/if}
            </div>

            <div class="form-field full-width">
              <label for="contactPosition">Position</label>
              <input
                type="text"
                id="contactPosition"
                bind:value={formData.contactPosition}
                placeholder="e.g., Owner, Accounts Manager"
              />
            </div>
          </div>
        </div>

        <!-- Billing Address -->
        <div class="form-section">
          <h2>Billing Address</h2>
          <div class="form-grid">
            <div class="form-field full-width">
              <label for="billingAddressLine1">Address Line 1 *</label>
              <input
                type="text"
                id="billingAddressLine1"
                bind:value={formData.billingAddressLine1}
                class:error={formErrors.billingAddressLine1}
                placeholder="Street address"
              />
              {#if formErrors.billingAddressLine1}<span class="field-error">{formErrors.billingAddressLine1}</span>{/if}
            </div>

            <div class="form-field full-width">
              <label for="billingAddressLine2">Address Line 2</label>
              <input
                type="text"
                id="billingAddressLine2"
                bind:value={formData.billingAddressLine2}
                placeholder="Unit, suite, etc."
              />
            </div>

            <div class="form-field">
              <label for="billingSuburb">Suburb *</label>
              <input
                type="text"
                id="billingSuburb"
                bind:value={formData.billingSuburb}
                class:error={formErrors.billingSuburb}
                placeholder="Enter suburb"
              />
              {#if formErrors.billingSuburb}<span class="field-error">{formErrors.billingSuburb}</span>{/if}
            </div>

            <div class="form-field">
              <label for="billingState">State *</label>
              <select
                id="billingState"
                bind:value={formData.billingState}
                class:error={formErrors.billingState}
              >
                <option value="">Select state</option>
                {#each AUSTRALIAN_STATES as state}
                  <option value={state.value}>{state.label}</option>
                {/each}
              </select>
              {#if formErrors.billingState}<span class="field-error">{formErrors.billingState}</span>{/if}
            </div>

            <div class="form-field">
              <label for="billingPostcode">Postcode *</label>
              <input
                type="text"
                id="billingPostcode"
                bind:value={formData.billingPostcode}
                class:error={formErrors.billingPostcode}
                placeholder="0000"
                maxlength="4"
              />
              {#if formErrors.billingPostcode}<span class="field-error">{formErrors.billingPostcode}</span>{/if}
            </div>

            <div class="form-field">
              <label for="billingCountry">Country</label>
              <input
                type="text"
                id="billingCountry"
                bind:value={formData.billingCountry}
                disabled
              />
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="submit-btn" disabled={submitting}>
            {#if submitting}
              <span class="btn-spinner"></span>
              Submitting...
            {:else}
              Submit Application
            {/if}
          </button>
        </div>
      </form>
    {/if}
  {/if}
</div>

<style>
  .credit-container {
    max-width: 800px;
  }

  .page-header {
    margin-bottom: 2rem;
  }

  .page-header h1 {
    margin: 0 0 0.5rem;
    font-size: 1.75rem;
    color: #333;
  }

  .page-header p {
    margin: 0;
    color: #666;
  }

  /* Loading State */
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-state p {
    margin-top: 1rem;
    color: #666;
  }

  /* Status Card */
  .status-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .status-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .status-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .status-icon svg {
    width: 24px;
    height: 24px;
  }

  .status-text {
    flex: 1;
  }

  .status-text h2 {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
    color: #333;
  }

  .status-text p {
    margin: 0;
    font-size: 0.875rem;
    color: #666;
  }

  .status-badge {
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .application-details {
    padding: 1.5rem 0;
  }

  .detail-section {
    margin-bottom: 1.5rem;
  }

  .detail-section:last-child {
    margin-bottom: 0;
  }

  .detail-section h3 {
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .detail-item label {
    display: block;
    font-size: 0.75rem;
    color: #999;
    margin-bottom: 0.25rem;
  }

  .detail-item span {
    font-size: 0.9375rem;
    color: #333;
  }

  .address-text {
    margin: 0;
    font-size: 0.9375rem;
    color: #333;
    line-height: 1.6;
  }

  .status-note {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    background: #fef3c7;
    border-radius: 8px;
    margin-top: 1rem;
  }

  .status-note.success {
    background: #d1fae5;
  }

  .status-note svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: #d97706;
  }

  .status-note.success svg {
    color: #059669;
  }

  .status-note p {
    margin: 0;
    font-size: 0.875rem;
    color: #92400e;
  }

  .status-note.success p {
    color: #065f46;
  }

  /* Rejection Notice */
  .rejection-notice {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    margin-bottom: 1.5rem;
  }

  .notice-icon {
    width: 40px;
    height: 40px;
    background: #fee2e2;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .notice-icon svg {
    width: 20px;
    height: 20px;
    color: #dc2626;
  }

  .notice-content h3 {
    margin: 0 0 0.25rem;
    font-size: 0.9375rem;
    color: #991b1b;
  }

  .notice-content p {
    margin: 0;
    font-size: 0.875rem;
    color: #b91c1c;
  }

  /* Success Message */
  .success-message {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    background: #d1fae5;
    border: 1px solid #a7f3d0;
    border-radius: 12px;
  }

  .success-message svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: #059669;
  }

  .success-message p {
    margin: 0;
    font-size: 1rem;
    color: #065f46;
  }

  /* Form */
  .application-form {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    padding: 0.75rem 1rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
  }

  .form-section {
    margin-bottom: 2rem;
  }

  .form-section:last-of-type {
    margin-bottom: 1.5rem;
  }

  .form-section h2 {
    margin: 0 0 1rem;
    font-size: 1.125rem;
    color: #333;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .form-field.full-width {
    grid-column: 1 / -1;
  }

  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .form-field input,
  .form-field select {
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.9375rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .form-field input:focus,
  .form-field select:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
  }

  .form-field input.error,
  .form-field select.error {
    border-color: #ef4444;
  }

  .form-field input:disabled {
    background: #f3f4f6;
    color: #6b7280;
  }

  .field-error {
    font-size: 0.75rem;
    color: #ef4444;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  .submit-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 2rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 8px;
    color: white;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .submit-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .btn-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @media (max-width: 640px) {
    .form-grid {
      grid-template-columns: 1fr;
    }

    .status-header {
      flex-direction: column;
      text-align: center;
    }

    .status-badge {
      align-self: center;
    }
  }
</style>
